import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import Cookies from "js-cookie";
import { kioskModel, teleConsultationModel } from "../Axios/AxiosConfig";
import { getDynamicLogo } from '../getDynamicLogo';
import { CircularProgress, IconButton } from "@mui/material";
import { format, startOfWeek, endOfWeek, isWithinInterval } from "date-fns";
import { ChevronLeft } from '@mui/icons-material';
import usePrescriptionStore from "../DataRepository/Prescription";

const getKioskStyles = (name) => {
    if (!name) return { bgcolor: "#f5f5f5", color: "#666" };
    const colors = [
        { bgcolor: "#FBF1E7", color: "#E69E5C" }, // goldorange50 / goldorange300
        { bgcolor: "#DBF9DB", color: "#1C8686" }, // mildgreen / goldorange (teal)
        { bgcolor: "#8CEB8C", color: "#3B362A" }, // ulvalactucagreen / sensaichabrown
        { bgcolor: "#FDFDFD", color: "#1C8686" }, // brilliance / goldorange (teal)
        { bgcolor: "#F4ECD6", color: "#E69E5C" }, // blondecurl200 / goldorange300
        { bgcolor: "#DBF9DB", color: "#75623F" }, // mildgreen / YellowMetal
        { bgcolor: "#FBF1E7", color: "#1C8686" }, // goldorange50 / goldorange (teal)
        { bgcolor: "#E7E7DB", color: "#E69E5C" }, // blondecurl / goldorange300
        { bgcolor: "#8CEB8C", color: "#75623F" }, // ulvalactucagreen / YellowMetal
        { bgcolor: "#F8F2E3", color: "#1C8686" }, // blondecurl300 / goldorange (teal)
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % colors.length;
    return colors[index];
};

const AllAppointmentsPage = () => {
    const [appointments, setAppointments] = useState([]);
    const [filteredAppointments, setFilteredAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [visibleCount, setVisibleCount] = useState();
    const [searchTerm, setSearchTerm] = useState("");
    const [filterDate, setFilterDate] = useState("all");

    const navigate = useNavigate();
    const { setSelectedAppointemntdetails } = usePrescriptionStore();

    const observer = useRef();

    const doctorDetailsCookie = Cookies.get("Doctor_details");
    let Doctor_details = {};

    if (doctorDetailsCookie) {
        try {
            Doctor_details = JSON.parse(doctorDetailsCookie);
        } catch (error) {
            console.error("Error parsing Doctor_details cookie:", error);
        }
    }


    const kioskDetailsCookie = Cookies.get("kiosk_details");
    let kiosk_details = {};

    if (kioskDetailsCookie) {
        try {
            kiosk_details = JSON.parse(kioskDetailsCookie);
        } catch (error) {
            console.log("Error parsing kiosk_details cookie:", error);
        }
    }

    const getAppointmentData = async () => {
        try {
            const res = await kioskModel.post("/getAllClinicalPatients", {
                kiosk_id: kiosk_details?.kiosk_id,

            });

            if (res.status === 200) {
                const sortedData = (res.data || []).sort((a, b) => {
                    const dateA = new Date(a.updated_at || a.created_at || 0);
                    const dateB = new Date(b.updated_at || b.created_at || 0);
                    return dateB - dateA; // Descending order: most recent first
                });
                setAppointments(sortedData);
                setFilteredAppointments(sortedData); // Initialize search data
            } else {
                console.error("Failed to fetch appointments:", res.statusText);
            }
        } catch (err) {
            console.error("Error fetching appointments:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getAppointmentData();
    }, []);

    // Handle search and filter
    useEffect(() => {
        let filtered = appointments.filter((appointment) =>
            appointment.firstname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            appointment.created_at?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            appointment.kiosk_id?.toLowerCase().includes(searchTerm.toLowerCase())
        );

        // Apply date filter
        if (filterDate !== "all") {
            filtered = filtered.filter((appointment) => {
                const appointmentDate = new Date(appointment.created_at);
                const now = new Date();
                let start, end;

                switch (filterDate) {
                    case "month":
                        start = new Date(now.getFullYear(), now.getMonth(), 1);
                        end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                        break;
                    case "year":
                        start = new Date(now.getFullYear(), 0, 1);
                        end = new Date(now.getFullYear(), 11, 31);
                        break;
                    case "week":
                        start = startOfWeek(now);
                        end = endOfWeek(now);
                        break;
                    case "day":
                        start = new Date(now.setHours(0, 0, 0, 0));
                        end = new Date(now.setHours(23, 59, 59, 999));
                        break;
                    default:
                        return true;
                }

                return isWithinInterval(appointmentDate, { start, end });
            });
        }

        setFilteredAppointments(filtered);
        setVisibleCount(10); // Reset visible count on search/filter change
    }, [searchTerm, filterDate, appointments]);

    // Infinite Scroll: Load more items when reaching the end
    const lastElementRef = useCallback(
        (node) => {
            if (loading) return;
            if (observer.current) observer.current.disconnect();
            observer.current = new IntersectionObserver((entries) => {
                if (entries[0].isIntersecting) {
                    setVisibleCount((prev) => prev + 10); // Load 10 more items
                }
            });
            if (node) observer.current.observe(node);
        },
        [loading]
    );

    function calculateAge(dob) {

        if (!dob) {
            return "";
        }

        let day, month, year;

        if (dob.includes('/')) {
            // Format: DD/MM/YYYY
            [day, month, year] = dob.split('/').map(part => parseInt(part, 10));
        } else if (dob.length === 8) {
            // Format: DDMMYYYY
            day = parseInt(dob.substring(0, 2), 10);
            month = parseInt(dob.substring(2, 4), 10);
            year = parseInt(dob.substring(4, 8), 10);
        } else {
            return "";
        }

        const dobDate = new Date(year, month - 1, day);

        const today = new Date();

        let age = today.getFullYear() - dobDate.getFullYear();

        const hasBirthdayPassed =
            today.getMonth() > dobDate.getMonth() ||
            (today.getMonth() === dobDate.getMonth() && today.getDate() >= dobDate.getDate());

        if (!hasBirthdayPassed) {
            age--;
        }

        return `${age.toString()} years old`;

    }

    return (
        <>
            <nav className='flex items-center p-2 mt-2 ml-2 mr-2 border rounded-lg cursor-pointer'>
                <Link to="/Appointments" className="flex items-center w-full">
                    <IconButton>
                        <ChevronLeft />
                    </IconButton>
                    <p className="ml-2 text-lg font-medium">Appointment</p>
                </Link>
            </nav>

            <div className="m-5 bg-[color:var(--themeSurfaceColor,#FFFFFF)] p-6 rounded-lg shadow-lg">
                <div className="flex justify-between items-center border-b pb-4 mb-4">
                    <h2 className="text-xl font-semibold text-[color:var(--themePrimaryColor,#374151)]">Doctor's Appointments</h2>
                    <div className="flex space-x-4">
                        <input
                            type="text"
                            placeholder="Search by Patient Name..."
                            className="border px-3 py-2 rounded-lg outline-none"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <select
                            className="border px-3 py-2 rounded-lg outline-none"
                            value={filterDate}
                            onChange={(e) => setFilterDate(e.target.value)}
                        >
                            <option value="all">All Time</option>
                            <option value="day">Today</option>
                            <option value="week">This Week</option>
                            <option value="month">This Month</option>
                            <option value="year">This Year</option>
                        </select>
                    </div>
                </div>

                {loading ? (
                    <p className="text-center text-[color:var(--themePrimaryColor,#6B7280)]">Loading appointments...</p>
                ) : filteredAppointments.length === 0 ? (
                    <p className="text-center font-semibold text-[color:var(--themePrimaryColor,#374151)]">No appointments found</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full border border-[color:var(--themePrimaryColor,#d1d5db)] rounded-lg">
                            <thead className="bg-[color:var(--themeSecondaryColor,#e5e7eb)] text-[color:var(--themePrimaryColor,#374151)]">
                                <tr>
                                    <th className="px-4 py-2 border border-[color:var(--themePrimaryColor,#d1d5db)] text-center">Date</th>

                                    <th className="px-4 py-2 border border-[color:var(--themePrimaryColor,#d1d5db)]">Patient Name</th>
                                    <th className="px-4 py-2 border border-[color:var(--themePrimaryColor,#d1d5db)]">Gender</th>
                                    <th className="px-4 py-2 border border-[color:var(--themePrimaryColor,#d1d5db)]">Age</th>
                                    <th className="px-4 py-2 border border-[color:var(--themePrimaryColor,#d1d5db)]"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredAppointments.slice(0, visibleCount).map((appointment, index) => (
                                    <tr
                                        key={index}
                                        className="border border-[color:var(--themePrimaryColor,#d1d5db)] hover:bg-[color:var(--themeSecondaryColor,#f3f4f6)]"
                                        ref={index === visibleCount - 1 ? lastElementRef : null}
                                    >
                                        <td className="px-4 py-2 border border-[color:var(--themePrimaryColor,#d1d5db)] text-center">
                                            {appointment.created_at
                                                ? format(new Date(appointment.created_at), "dd-MMM-yyyy")
                                                : "N/A"}
                                        </td>

                                        <td className="px-4 py-2 border border-[color:var(--themePrimaryColor,#d1d5db)] text-center">
                                            {appointment?.firstname ?? "N/A" + " " + (appointment?.lastname ?? "N/A")}
                                        </td>
                                        <td className="px-4 py-2 border border-[color:var(--themePrimaryColor,#d1d5db)] text-center">
                                            {appointment?.gender ?? "N/A"}
                                        </td>
                                        <td className="px-4 py-2 border border-[color:var(--themePrimaryColor,#d1d5db)] text-center">
                                            {calculateAge(appointment?.dob) ?? "N/A"}
                                        </td>
                                        <td className="px-4 py-2 border border-[color:var(--themePrimaryColor,#d1d5db)] text-center">
                                            <button
                                                onClick={() => {
                                                    setSelectedAppointemntdetails(appointment);
                                                    navigate(`/patient/${appointment?.UID}`);
                                                }}
                                                className="px-3 py-2 border border-orange-500 text-orange-500 font-medium rounded hover:bg-orange-500 hover:text-white"
                                            >
                                                View Profile
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {loading && (
                    <div
                        style={{
                            position: "fixed",
                            top: 0,
                            left: 0,
                            width: "100vw",
                            height: "100vh",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            backgroundColor: "var(--themeSurfaceColor, #FDFDFD)",
                            zIndex: 9999,
                        }}
                    >
                        <div style={{ position: "relative", width: "10rem", height: "10rem" }}>
                            <CircularProgress style={{ width: "100%", height: "100%", color: "var(--themePrimaryColor, #DA6E0B)" }} thickness={1} />
                            <div
                                style={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                }}
                            >
                                <img src={getDynamicLogo()} style={{ objectFit: 'contain', width: "80%", height: "80%" }} alt="Loading" />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default AllAppointmentsPage;
